package com.milta.core.localization

data class CommonStrings(
    val appName: String,
    val ok: String,
    val cancel: String,
    val confirm: String,
    val save: String,
    val delete: String,
    val back: String,
    val loading: String,
    val retry: String,
    /** Under every "analysing" card — extraction AND HERE address validation run in the same call, so
     * closing the app/window would lose the whole import. */
    val creationInProgressDoNotClose: String,
    /** MiltaProcessingCard — étapes affichées pendant une analyse (photo/PDF/tableur). L'avancement
     * est estimé côté client : le backend ne renvoie aucune progression aujourd'hui. */
    val processingStepSent: String,
    val processingStepReading: String,
    val processingStepFinding: String,
    val processingStepChecking: String,
    /** Toujours visible pendant l'analyse : durée habituelle + consigne de rester sur l'écran. */
    val processingUsualDuration: String,
    /** Remplace processingUsualDuration une fois la durée habituelle dépassée — ton informatif, jamais une erreur. */
    val processingLongWait: String,
    /** Retour 26/10 — real progress (step details of MiltaProcessingCard / the web analysis). "%1" = m:ss. */
    val processingInProgressSince: String,
    val processingFinishedIn: String,
    /** "%1" = count, plural; [processingDeliveriesFoundOne] when it is exactly 1. */
    val processingDeliveriesFound: String,
    val processingDeliveriesFoundOne: String,
    /** "%1" addresses checked out of "%2". */
    val processingAddressesChecked: String,
    /** Per-row HERE validation badges on the import/scan review screens (never a replacement address). */
    val addressVerifiedBadge: String,
    val addressNeedsReviewBadge: String,
    /** Under a verified address whose HERE match differs from the text typed: "Trouvée : %1". */
    val addressFoundLine: String,
    val language: String,
    val welcomeTitle: String,
    val welcomeSubtitle: String,
    val alreadyHaveAccount: String,
    val createAccount: String,
    val continueWithGoogle: String,
    val continueWithApple: String,
    val oauthErrorGeneric: String,
    /** Bouton Apple visible et localisé sur le web, mais l'action n'est pas encore câblée
     * (le relais backend `OAuthController` ne redirige aujourd'hui que vers un deep link mobile). */
    val oauthAppleWebComingSoon: String,
    /** Le dashboard web est réservé aux comptes Enterprise (les chauffeurs utilisent l'app mobile) —
     * affiché quand un compte Driver-only s'authentifie ici ; la session est aussitôt révoquée
     * côté client (voir `webApp/.../Main.kt`, `requireEnterprise`). */
    val oauthDriverAccountNotAllowedOnWeb: String,
    /** Le web ne crée jamais de compte (décision produit) : contrairement au mobile, un compte
     * Google non enregistré n'ouvre pas de formulaire d'inscription — juste ce message d'erreur,
     * on reste sur Welcome (voir `webApp/.../Main.kt`, cas `OAuthAuthenticateOutcome.NotRegistered`). */
    val oauthAccountNotFoundOnWeb: String,
    /** Mode hors ligne — quand un GET échoue sans réponse HTTP du tout (pas de réseau), distinct
     * d'une vraie erreur serveur (voir les `mapError` de RouteInProgressViewModel/ProfileViewModel/
     * TeamViewModel/etc.). */
    val errorOffline: String,
    // Dashboard web (MIL-47/DOM-06) — labels de la nav principale du DashboardShell. "Équipe"
    // n'est PAS dupliqué ici : réutilise team.screenTitle (voir webApp/.../DashboardShell.kt).
    val navTourneesDuJour: String,
    val navAjouterUneTournee: String,
    val navHistoriqueDesPreuves: String,
    val navParametres: String,
    /** MIL-48 — role label shown under the user's name in [com.milta.web.DashboardShell]'s sidebar
     * footer. Deliberately its own key rather than reusing [ProfileStrings.roleOrganisation] ("Entreprise"/
     * "Enterprise"), which is shared with the mobile [com.milta.feature.profile.presentation.ProfileScreen]:
     * the ticket asks for "Gestionnaire" in this one sidebar spot on web only, without touching the
     * mobile-shared wording. */
    val sidebarRoleManagerLabel: String,
    /** Empty-state shown on [com.milta.feature.route.presentation.CreateRouteScreen]'s "Livraisons"
     * section before any delivery has been added — kept here rather than in RouteStrings, which is
     * at its 254/255 DEX field-count ceiling (see CreateRouteScreen.kt history). */
    val noDeliveriesYetTitle: String,
    val noDeliveriesYetSubtitle: String,
    /** [com.milta.feature.route.presentation.CreateRouteState.optimizationUnavailable] dialog —
     * body text; the title reuses RouteStrings.errorOptimizationUnavailable, and the "retry"
     * button reuses [retry] above, to stay under RouteStrings' 254/255 DEX field ceiling. */
    val optimizationUnavailableExplanation: String,
    val createWithoutOptimizationAction: String,
    /** UX-DR5 — [com.milta.feature.route.presentation.GpsAppSheet] title, shown when more than
     * one GPS app is installed (StopDetailScreen "Naviguer"). Kept here rather than in
     * RouteStrings, which is at its 254/255 DEX field-count ceiling (see above). */
    val gpsAppSheetTitle: String,
    /** [com.milta.feature.route.presentation.DeliveryEntryScreen] address-autocomplete dropdown —
     * shown while a suggestions request is in flight. Kept here rather than in RouteStrings, which
     * is at its 254/255 DEX field-count ceiling (see above). */
    val addressSearchingLabel: String,
    /** Retour client 17/09/2026, point 11 — [com.milta.core.ui.component.TimeField]'s clear-icon
     * content description and confirmation dialog, shown before blanking a deadline that was
     * already set (whether OCR-detected or manually entered — the field carries no origin flag). */
    val clearTimeFieldAction: String,
    val clearTimeFieldConfirmTitle: String,
    val clearTimeFieldConfirmMessage: String,
    // ── Mandatory update (store version newer than the installed one) ──
    val updateRequiredTitle: String,
    val updateRequiredMessage: String,
    /** "Installée 1.0.35 · Disponible 1.0.36" — the two labels. */
    val updateInstalledVersionLabel: String,
    val updateAvailableVersionLabel: String,
    val updateNowButton: String,
    val updateDataKeptNote: String,
    /** While the offline validations are sent before the update: 1 / {count} deliveries. */
    val updateSendingPendingOne: String,
    val updateSendingPendingMany: String,
)

/** "Envoi de 1 livraison validée hors ligne…" / "Envoi de N livraisons validées hors ligne…". */
fun CommonStrings.updateSendingPendingText(count: Int): String =
    if (count == 1) updateSendingPendingOne else updateSendingPendingMany.replace("{count}", count.toString())
